import { types, flow, applySnapshot } from "mobx-state-tree";

import { updateCampaign } from "api/campaigns";
import { compareArrays } from "util/array-utils";

import BaseBackboneModelMigrationStore from "pages/common/state/store/backbone-model-template";

export const CONSTANTS = {
    GRABYO_DOMAIN: `grabyo.com`,
    STATUS: {
        ACTIVE: `active`,
        ARCHIVED: `archived`
    },
    GEO_TARGETING_TYPE: {
        WHITELIST: `whitelist`,
        BLACKLIST: `blacklist`
    },
    CAMPAIGN_TYPE: {
        GALLERY: `gallery`,
        BRAND: `brand`,
        EVENT: `event`,
        CHANNEL: `channel`
    }
};

export const DEFAULTS = {
    COVER_IMAGE: `/static/img/cards-campaign.png`,
    GEO_TARGETING_TYPE: CONSTANTS.GEO_TARGETING_TYPE.WHITELIST,
    TYPE: CONSTANTS.CAMPAIGN_TYPE.GALLERY
};

const model = {
    contextId: types.identifier,
    type: types.optional(types.string, DEFAULTS.TYPE),
    coverImage: types.optional(types.string, DEFAULTS.COVER_IMAGE),
    publicGalleryEnabled: types.maybe(types.boolean),
    displayName: types.maybe(types.string),
    creationDate: types.maybe(types.integer),
    waitsForMultiBitRateTranscoding: types.maybe(types.string),
    organizationId: types.maybe(types.string),
    closedCaptionsEnabled: types.maybe(types.boolean),
    galleryExtension: types.maybe(types.frozen()),
    inlineTypeConfig: types.maybe(types.frozen()),
    twitterCardConfig: types.maybe(types.frozen()),
    facebookPostConfig: types.maybe(types.frozen()),
    renderConfig: types.maybe(types.frozen()),
    name: types.maybe(types.string),
    encodingProfile: types.maybe(types.string),
    status: types.maybe(types.string),
    allowedCountries: types.optional(types.array(types.string), []),
    disallowedCountries: types.optional(types.array(types.string), []),
    playerBrandingConfig: types.maybe(types.string),
    aliasId: types.maybe(types.string),
    defaultShareTitle: types.maybe(types.string),
    defaultShareDescription: types.maybe(types.string),
    notificationsConfig: types.maybe(types.string),
    domainOverride: types.maybe(types.string),
    featuresConfig: types.maybe(types.frozen())
};

const volatile = () => ({
    campaignId: undefined,
    brandId: undefined,
    eventId: undefined,
    channelId: undefined,
    galleryId: undefined,
    identifierAttribute: undefined,
    urlPrefix: ``
});

const preProcessor = (snapshot) => {
    if (snapshot.contextId) return snapshot;

    let contextId;
    const { galleryId, channelId, brandId, eventId } = snapshot;
    const campaignId = galleryId ?? channelId ?? brandId ?? eventId;

    switch (campaignId) {
        case galleryId:
            contextId = `gallery:${campaignId}`;
            break;
        case channelId:
            contextId = `channel:${campaignId}`;
            break;
        case brandId:
            contextId = `brand:${campaignId}`;
            break;
        case eventId:
            contextId = `event:${campaignId}`;
            break;
    }
    return {
        ...snapshot,
        contextId
    };
};

const views = (self) => ({
    get targetedCountries() {
        if (self.geoTargetingType === CONSTANTS.GEO_TARGETING_TYPE.BLACKLIST) {
            return self.disallowedCountries ?? [];
        } else {
            return self.allowedCountries ?? [];
        }
    },

    get geoTargetingType() {
        if (self?.disallowedCountries?.length) {
            return CONSTANTS.GEO_TARGETING_TYPE.BLACKLIST;
        } else {
            return CONSTANTS.GEO_TARGETING_TYPE.WHITELIST;
        }
    },

    get compoundId() {
        return self.contextId;
    },

    get id() {
        return self.campaignId;
    },

    get hasAliasId() {
        return self.aliasId?.length ? true : false;
    },

    get hasDomainOverride() {
        return self.domainOverride?.length ? true : false;
    },

    get isGeotargeted() {
        return self.targetedCountries?.length ? true : false;
    },

    get isActive() {
        return self.status === CONSTANTS.STATUS.ACTIVE;
    },

    get isArchived() {
        return self.status === CONSTANTS.STATUS.ARCHIVED;
    },

    get hasExtension() {
        return self.galleryExtension ? true : false;
    },

    get formData() {
        const data = {
            id: self.campaignId,
            compoundId: self.compoundId,
            name: self.name,
            targetedCountries: self.targetedCountries,
            geoTargetingType: self.geoTargetingType,
            publicName: self.getDisplayName(),
            type: self.type,
            coverImage: self.coverImage
        };

        if (self.facebookPostConfig) {
            const facebookPostConfig = JSON.parse(self?.facebookPostConfig);
            data.defaultFacebookUserId = facebookPostConfig.defaultMentionId;
            data.defaultFacebookUserName =
                facebookPostConfig.defaultMentionName;
        }

        if (self.twitterCardConfig) {
            const twitterCardConfig = JSON.parse(self?.twitterCardConfig);
            data.defaultTwitterUser = twitterCardConfig.site;
        }

        if (self?.renderConfig?.background) {
            data.backgroundColor = self.renderConfig.background;
        }

        return data;
    },

    getPublicGalleryDomainWithoutPrefix() {
        let url = self.hasDomainOverride
            ? self.domainOverride
            : CONSTANTS.GRABYO_DOMAIN;

        // If we're in development, then set the domain to be our current instance
        if (
            self.hasDomainOverride &&
            window?.data.environment === `development`
        ) {
            url = location.host;
        }

        // Check if protocol is present
        if (!url.toLowerCase().match(/^http/)) {
            url = `https://${url}`;
        }

        // Check if the domain doesn't contain a trailing slash
        if (url.charAt(url.length - 1) !== `/`) {
            return `${url}/`;
        }

        return `${url}g/`;
    },

    getPublicGalleryDomain() {
        const url = self.getPublicGalleryDomainWithoutPrefix();

        if (self.hasAliasId) {
            return `${url}${self.urlPrefix}/`;
        }

        return url;
    },

    getPublicContextUrl() {
        const url = self.hasAliasId
            ? `${self.getPublicGalleryDomain()}${self.aliasId}`
            : self.campaignId;

        return url;
    },

    getName() {
        return self.name || self.campaignId;
    },

    getDisplayName() {
        return self.displayName || self.name;
    },

    getEncodingProfile() {
        return self.encodingProfile;
    },

    toFormFormat() {
        return self.formData;
    },

    getCompoundId() {
        return self.contextId;
    },

    /** TODO: Create a global utility function to manage query params **/
    generateQueryParam() {
        const params = {
            campaignId: self.campaignId
        };
        if (App.Utils?.params?.streamId) {
            params.streamId = App.Utils.params.streamId;
        }
        const queryStr = Object.keys(params).reduce((str, key) => {
            return str.length
                ? `${str}&${key}=${params[key]}`
                : `${key}=${params[key]}`;
        }, ``);
        return queryStr;
    },

    isDefault() {
        return (
            App.Utils?.params?.id === self.campaignId ||
            App.Utils?.params?.campaignId === self.campaignId
        );
    }
});

const actions = (self) => ({
    afterCreate() {
        self.initDerivedProps();
        self.initGeoSettings();
    },

    /**
     * Update campaign with new data
     * @param {CampaignObject} data
     */
    refresh(data) {
        applySnapshot(self, data);
        self.initDerivedProps();
        self.initGeoSettings();
    },

    /**
     * WARNING - allowedCountries and disallowedCountries should never co-exists.
     * If there's allowedCountries, there should never be disallowedCountries and vice versa.
     * This prioritises allowedCountries over disallowedCountries.
     */
    initGeoSettings() {
        if (self.allowedCountries?.length) {
            self.disallowedCountries = [];
        } else if (self.disallowedCountries?.length) {
            self.allowedCountries = [];
        }
    },

    /**
     * This initialises properties derived from the campaign type. Although only the `gallery` type is currently used,
     * we still need to support the other lagacy types, as there are a small number of clients (e.g. ITV) that
     * may still have old campaigns associated with them
     */
    initDerivedProps() {
        self.type = self.contextId.split(`:`)[0];
        self.identifierAttribute = `${self.type}Id`;
        self.campaignId = self.contextId.split(`:`)[1];
        self[self.identifierAttribute] = self.campaignId;
    }
});

const setterActions = (self) => ({
    setUrlPrefix(prefix) {
        self.urlPrefix = prefix;
    },
    setAllowedCountries(countries, validate = true) {
        if (!countries || compareArrays(countries, self.allowedCountries))
            return;

        self.allowedCountries = [...countries];
        if (validate) {
            self.setDisallowedCountries([], false);
        }
    },
    setDisallowedCountries(countries, validate = true) {
        if (!countries || compareArrays(countries, self.disallowedCountries))
            return;

        self.disallowedCountries = [...countries];
        if (validate) {
            self.setAllowedCountries([], false);
        }
    },
    setOrganizationId(id) {
        self.organizationId = id;
    }
});

const asyncActions = (self) => ({
    /**
     * Make an api call to update this campaign
     * @async
     * @param {Boolean} returnNode
     * @return {CampaignObject | Error}
     */
    update: flow(function* update(newData) {
        try {
            const { data } = yield updateCampaign(self.campaignId, newData);
            self.refresh(data);
            return self;
        } catch (error) {
            console.error(error);
            return error;
        }
    }),

    /**
     * Toggle campaign status between active/archived
     * @async
     * @return {CampaignObject | Error}
     */
    toggleStatus: flow(function* toggleStatus() {
        try {
            const newStatus =
                self.status === CONSTANTS.STATUS.ACTIVE
                    ? CONSTANTS.STATUS.ARCHIVED
                    : CONSTANTS.STATUS.ACTIVE;
            yield self.update({
                contextStatus: newStatus
            });
            return self;
        } catch (error) {
            console.error(error);
            return error;
        }
    })
});

const backboneUtilityActions = (self) => ({
    updateGallery(data) {
        self.update(data);
    }
});

const _CampaignStore = types
    .model(`Campaign`, model)
    .volatile(volatile)
    .views(views)
    .preProcessSnapshot(preProcessor)
    .actions(actions)
    .actions(setterActions)
    .actions(asyncActions)
    .actions(backboneUtilityActions);

export const CampaignStore = BaseBackboneModelMigrationStore(_CampaignStore);
